import { useCallback, useEffect, useMemo, useState } from "react";
import { HttpApiService } from "@/shared/services/httpApi.service";
import {
  shippingQuotesService,
  buildShippingQuotesQueryParams,
} from "../services/shippingQuotes.service";
import {
  QUOTER_HISTORY_PAGE_SIZE,
  PAGINATION_SIZES,
} from "../constants/quoter-history.constants";
import type { ShippingQuote } from "../types/shipping-quotes-api.types";
import type { QuoterHistoryFiltersState } from "../types/quoter-history.types";

export type SelectOption = { label: string; value: string };

function getDefaultFilters(): QuoterHistoryFiltersState {
  return {
    origin_currency: "BRL",
    destination_currency: "COP",
    start_date: "",
    end_date: "",
  };
}

function getPageNumbers(
  currentPage: number,
  lastPage: number,
  _canGoNext: boolean
): (number | string)[] {
  const maxPagesToShow = 7;
  if (lastPage <= maxPagesToShow) {
    return Array.from({ length: lastPage }, (_, i) => i + 1);
  }
  if (currentPage <= 3) {
    return [1, 2, 3, 4, "...", lastPage];
  }
  if (currentPage >= lastPage - 2) {
    return [1, "...", lastPage - 3, lastPage - 2, lastPage - 1, lastPage];
  }
  return [
    1,
    "...",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "...",
    lastPage,
  ];
}

export interface UseQuoterHistoryPageReturn {
  filters: QuoterHistoryFiltersState;
  setFilters: React.Dispatch<
    React.SetStateAction<QuoterHistoryFiltersState>
  >;
  page: number;
  pageSize: number;
  rows: ShippingQuote[];
  totalItems: number;
  lastPage: number;
  canGoPrevious: boolean;
  canGoNext: boolean;
  from: number;
  to: number;
  isLoading: boolean;
  error: string | null;
  paginationOptions: SelectOption[];
  pageNumbers: (number | string)[];
  handleApplyFilters: () => void;
  handleResetFilters: () => void;
  handlePageChange: (page: number) => void;
  handleItemsPerPageChange: (value: string) => void;
  refetch: () => void;
  noAccess: boolean;
}

export function useQuoterHistoryPage(): UseQuoterHistoryPageReturn {
  const [filters, setFilters] = useState<QuoterHistoryFiltersState>(
    getDefaultFilters
  );
  const [appliedFilters, setAppliedFilters] =
    useState<QuoterHistoryFiltersState>(getDefaultFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(QUOTER_HISTORY_PAGE_SIZE);
  const [rows, setRows] = useState<ShippingQuote[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noAccess, setNoAccess] = useState(false);

  const fetchList = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setNoAccess(false);
    try {
      const params = buildShippingQuotesQueryParams(
        appliedFilters,
        page,
        pageSize
      );
      const response = await shippingQuotesService.fetchList(params);
      setRows(response.data);
      setTotalItems(response.total);
    } catch (err) {
      if (HttpApiService.isAccessDeniedError(err)) {
        setNoAccess(true);
        setRows([]);
        setTotalItems(0);
        return;
      }
      setError(
        HttpApiService.getErrorMessage(err) ||
          "Error al cargar el historial de cotizaciones"
      );
      setRows([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, appliedFilters]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const lastPage = Math.max(1, Math.ceil(totalItems / pageSize));
  const canGoPrevious = page > 1;
  const canGoNext = page < lastPage;
  const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);

  const paginationOptions = useMemo(
    () => PAGINATION_SIZES.map((size) => ({ label: String(size), value: String(size) })),
    []
  );

  const pageNumbers = useMemo(
    () => getPageNumbers(page, lastPage, canGoNext),
    [page, lastPage, canGoNext]
  );

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters(filters);
    setPage(1);
  }, [filters]);
  const handleResetFilters = useCallback(() => {
    const empty = getDefaultFilters();
    setFilters(empty);
    setAppliedFilters(empty);
    setPage(1);
  }, []);
  const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);
  const handleItemsPerPageChange = useCallback((value: string) => {
    setPageSize(Number(value));
    setPage(1);
  }, []);

  return {
    filters,
    setFilters,
    page,
    pageSize,
    rows,
    totalItems,
    lastPage,
    canGoPrevious,
    canGoNext,
    from,
    to,
    isLoading,
    error,
    paginationOptions,
    pageNumbers,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleItemsPerPageChange,
    refetch: fetchList,
    noAccess,
  };
}
